import { z } from "zod";

const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // Matches YYYY-MM-DD
const timestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z?$/; // Matches full ISO timestamp

export const ValidationSchemas = {
  string: z.string().catch(""),
  number: z.number().catch(null),
  email: z.string().email("Invalid email format").or(z.literal("")).optional(),
  boolean: z.boolean().catch(false),
  positiveInt: z.number().int().nonnegative(),
  coordinate: {
    lat: z
      .number()
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90"),
    lon: z
      .number()
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180"),
  },
  date: z
    .string()
    .refine(
      (val) =>
        dateRegex.test(val) ||
        timestampRegex.test(val) ||
        z.string().datetime(),
      "Invalid date format. Expected 'YYYY-MM-DD' or ISO timestamp."
    ),
  datetime: z.string().datetime().catch(null),
  readOnlyNullableString: z.string().nullable().default(null),
  readOnlyNullableNumber: z.number().nullable().default(null),
};

export const lookupConfig = {
  wellUse: {
    tableName: "fs_lookup_well_use",
    idField: "well_use_ndx",
    labelField: "well_use_name",
  },
  wellStatus: {
    tableName: "fs_lookup_well_status",
    idField: "status_ndx",
    labelField: "status_type_desc",
  },
  monitoringStatus: {
    tableName: "fs_lookup_monitoring_status",
    idField: "monitoring_status_ndx",
    labelField: "monitoring_status_desc",
  },
  systemTypes: {
    tableName: "fs_lookup_system_types",
    idField: "system_types_ndx",
    labelField: "system_types_desc",
  },
  pumpTypes: {
    tableName: "fs_lookup_pump_types",
    idField: "pump_types_ndx",
    labelField: "pump_types_desc",
  },
  wellClassification: {
    tableName: "fs_lookup_well_classification",
    idField: "well_class_ndx",
    labelField: "well_class_name",
  },
  wellSubclass: {
    tableName: "fs_lookup_well_subclass",
    idField: "well_subclass_ndx",
    labelField: "well_subclass_name",
  },
  meters: {
    tableName: "fs_lookup_meters",
    idField: "meter_ndx",
    labelField: "meter_info",
    additionalFields: ["meter_sn", "meter_nickname"],
  },
  samplingSpigot: {
    tableName: "fs_lookup_sampling_spigot",
    idField: "sampling_spigot_ndx",
    labelField: "sampling_spigot_desc",
    additionalFields: ["installation_date", "material_type"],
  },
  otherTreatments: {
    tableName: "fs_lookup_other_treatments",
    idField: "other_treatments_ndx",
    labelField: "other_treatments_desc",
    additionalFields: ["treatment_type", "installation_date"],
  },
  casingTypes: {
    tableName: "fs_lookup_casing_types",
    idField: "casing_types_ndx",
    labelField: "casing_types_desc",
    additionalFields: ["material_type", "standard_size"],
  },
  adjustmentReason: {
    tableName: "ui_list_meter_reading_adjustment_reason",
    idField: "mr_adjustment_reason_ndx",
    labelField: "mr_adjustment_reason_desc",
  },
  reportStatus: {
    tableName: "ui_list_meter_reading_report_status",
    idField: "mr_report_status_ndx",
    labelField: "mr_report_status_desc",
  },
  inspectionVisitTypes: {
    tableName: "ui_list_ir_types",
    idField: "ir_type_ndx",
    labelField: "ir_type_desc",
  },
  waterLevelMeasurementMethods: {
    tableName: "ui_list_wl_meas_methods",
    idField: "wl_meas_method_ndx",
    labelField: "wl_meas_method_desc",
  },
  waterLevelMeasuredFrom: {
    tableName: "ui_list_wl_meas_from",
    idField: "wl_meas_from_ndx",
    labelField: "wl_meas_from_desc",
  },
  waterLevelSourceOfMeasurements: {
    tableName: "ui_list_wl_sources",
    idField: "aqsci_data_source_ndx",
    labelField: "aqsci_data_source_desc",
  },
  wqEquipment: {
    tableName: "ui_list_wq_equipment",
    idField: "aqsci_equipment_ndx",
    labelField: "aqsci_equipment_desc",
    additionalFields: ["is_other"],
  },
  wlEquipment: {
    tableName: "ui_list_wl_equipment",
    idField: "aqsci_equipment_ndx",
    labelField: "aqsci_equipment_desc",
    additionalFields: ["is_other"],
  },
};

export const formDefaults = {
  number: null,
  text: "",
  boolean: false,
  selectSingle: null,
  selectMulti: [],
  date: null,
  dateTime: null,
};
